// packages/shared/src/api/client.ts
//
// `apiClient` — the canonical openapi-fetch wrapper. Every FE call
// in the monorepo SHOULD route through this client so that:
//   1. Typing follows `paths` from `types.generated.ts` (regenerated via
//      `pnpm api:sync`).
//   2. Cookies (HttpOnly JWT + XSRF double-submit) ride every request via
//      `credentials: 'include'`.
//   3. The CSRF header (`X-XSRF-TOKEN`) is auto-attached on mutating verbs
//      from the matching readable cookie (mirrored on the
//      server by `@fastify/csrf-protection`).
//
// This module MUST stay node-pure (no React, no Fastify) so that BE callers +
// Vite bundlers + Next.js SSR can all consume it. `@printway/shared` is ESM,
// so direct `import.meta.env` probing is valid in both browser bundles and
// Node ESM; Node simply leaves `env` undefined.
import createClient from 'openapi-fetch';
// ---------------------------------------------------------------------------
// Base URL detection
// ---------------------------------------------------------------------------
/**
 * Resolve the API base URL with the following precedence:
 *   1. Explicit `opts.baseUrl` (caller override).
 *   2. Vite — `import.meta.env.VITE_API_URL`, then `VITE_API_BASE_URL`.
 *   3. Next.js / Node — `process.env.NEXT_PUBLIC_API_URL` then
 *      `process.env.API_URL`.
 *   4. Fallback `http://localhost:3010` (matches Mode A dev convention from
 *      `packages/shared/src/url.ts`).
 */
export function resolveApiBaseUrl(explicit) {
    if (explicit && explicit.length > 0)
        return explicit;
    const viteEnv = import.meta.env;
    const viteUrl = viteEnv?.VITE_API_URL ?? viteEnv?.VITE_API_BASE_URL;
    if (typeof viteUrl === 'string' && viteUrl.length > 0)
        return viteUrl;
    // Node / Next.js probe.
    if (typeof process !== 'undefined' && process.env) {
        const nextPublic = process.env.NEXT_PUBLIC_API_URL;
        if (typeof nextPublic === 'string' && nextPublic.length > 0) {
            return nextPublic;
        }
        const nodeUrl = process.env.API_URL;
        if (typeof nodeUrl === 'string' && nodeUrl.length > 0)
            return nodeUrl;
    }
    return 'http://localhost:3010';
}
// ---------------------------------------------------------------------------
// XSRF helper — cookie reader (double-submit)
// ---------------------------------------------------------------------------
/** Reads `XSRF-TOKEN` cookie value (NOT HttpOnly). */
function readXsrfCookie() {
    // Avoid pulling DOM lib types into @printway/shared (node-pure boundary
    // per FIX-13). Probe `globalThis.document` lazily — present only in the
    // browser; on Fastify / Next.js server we short-circuit to undefined.
    const doc = globalThis.document;
    if (!doc || typeof doc.cookie !== 'string')
        return undefined;
    const raw = doc.cookie;
    for (const part of raw.split('; ')) {
        const eq = part.indexOf('=');
        if (eq < 0)
            continue;
        const name = part.slice(0, eq);
        if (name === 'XSRF-TOKEN') {
            try {
                return decodeURIComponent(part.slice(eq + 1));
            }
            catch {
                return part.slice(eq + 1);
            }
        }
    }
    return undefined;
}
const MUTATING_VERBS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);
// ---------------------------------------------------------------------------
// 401 / token expiry handling — dispatch `printway:unauthorized` then redirect
// ---------------------------------------------------------------------------
//
// dbg-token-expiry-silent — vendor-v3 + admin-v3 both consume this client.
// Before this hook, an expired-token 401 silently surfaced as a thrown
// Error inside TanStack Query; the UI stayed on the current page until the
// user manually F5'd. Now: every 401 response (except auth probe endpoints)
// dispatches a cancelable `printway:unauthorized` CustomEvent so the host
// app can navigate via react-router. If no listener calls preventDefault(),
// we fall back to `window.location.href = '/login?returnUrl=...'` on the
// next microtask so the user is never left stranded.
//
// Auth probe paths are skipped to avoid redirect loops on the
// initial `whoami` / `refresh` / `login` round-trip.
// Auth-flow endpoints whose 401 is a NORMAL validation outcome (wrong/expired
// OTP, bad credentials), NOT a session expiry. Their 401 must be handled
// inline by the calling page — never trigger the global redirect-to-login,
// which strands the user mid-registration (dbg 20260703-verify-otp-redirect:
// entering an expired OTP bounced to /login?returnUrl=/verify-otp instead of
// showing "wrong OTP", leaving the account pending_otp → "Email chưa xác thực").
const AUTH_PROBE_PATTERN = /\/api\/v1\/auth\/(me|login|refresh|logout|csrf|register|verify-otp|resend-otp|verify-2fa|survey)\b/;
function getBrowserWindow() {
    const w = globalThis.window;
    if (!w || typeof w !== 'object')
        return null;
    if (!w.location || typeof w.dispatchEvent !== 'function')
        return null;
    return w;
}
function isAuthProbeUrl(url) {
    return AUTH_PROBE_PATTERN.test(url);
}
function isOnLoginRoute(win) {
    const path = win.location.pathname ?? '';
    return path === '/login' || path.startsWith('/login/') || path.startsWith('/login?');
}
function buildReturnUrl(win) {
    const { pathname, search, hash } = win.location;
    return `${pathname}${search}${hash}`;
}
/**
 * Dispatch the `printway:unauthorized` event then (as fallback) redirect to
 * `/login?returnUrl=<current>`. Apps that want react-router navigation can
 * subscribe via `window.addEventListener('printway:unauthorized', (e) => {
 *   e.preventDefault();
 *   navigate('/login?returnUrl=...');
 * })`.
 *
 * Exported for `csrfFetch` (apps/vendor) + any other caller that needs to
 * forward a raw fetch 401 into the shared flow.
 */
export function handleUnauthorized(url) {
    const win = getBrowserWindow();
    if (!win)
        return;
    if (isAuthProbeUrl(url))
        return;
    if (isOnLoginRoute(win))
        return;
    const event = new win.CustomEvent('printway:unauthorized', {
        detail: { url, status: 401 },
        cancelable: true,
    });
    const notPrevented = win.dispatchEvent(event);
    if (notPrevented) {
        // Defer so any synchronous react-router navigate inside the event
        // handler runs first; force a redirect only if nobody actually moved
        // the user off the protected page.
        queueMicrotask(() => {
            const w = getBrowserWindow();
            if (!w || isOnLoginRoute(w))
                return;
            const returnUrl = encodeURIComponent(buildReturnUrl(w));
            w.location.href = `/login?returnUrl=${returnUrl}`;
        });
    }
}
/**
 * Build a per-app openapi-fetch client. Typed by the shared `paths` schema
 * regenerated via `pnpm api:sync`. Adds:
 *   - `credentials: 'include'` (cookie auth)
 *   - `X-XSRF-TOKEN` header on mutating verbs
 *   - `Accept: application/json` default
 */
export function createApiClient(opts = {}) {
    const baseUrl = resolveApiBaseUrl(opts.baseUrl);
    const defaultHeaders = {
        Accept: 'application/json',
        ...(opts.headers ?? {}),
    };
    const client = createClient({
        baseUrl,
        credentials: 'include',
        headers: defaultHeaders,
        ...(opts.fetch ? { fetch: opts.fetch } : {}),
    });
    // Middleware — auto-attach XSRF on mutating verbs + intercept 401.
    client.use({
        onRequest({ request }) {
            const method = request.method.toUpperCase();
            if (!MUTATING_VERBS.has(method))
                return undefined;
            const xsrf = readXsrfCookie();
            if (xsrf && !request.headers.has('X-XSRF-TOKEN')) {
                request.headers.set('X-XSRF-TOKEN', xsrf);
            }
            return undefined;
        },
        onResponse({ request, response }) {
            // dbg-token-expiry-silent — trap 401 globally so vendor-v3 + admin-v3
            // auto-redirect to /login instead of leaving the user stuck on the
            // current page until they F5.
            if (response.status === 401) {
                const url = response.url || request.url || '';
                handleUnauthorized(url);
            }
            return undefined;
        },
    });
    return client;
}
// ---------------------------------------------------------------------------
// Default singleton — apps that don't need a custom client just import this.
// ---------------------------------------------------------------------------
/**
 * Default `apiClient = createClient<paths>({ baseUrl })`.
 * Lazy — first access constructs the singleton with env-resolved baseUrl.
 */
let _singleton;
export function apiClient() {
    if (!_singleton)
        _singleton = createApiClient();
    return _singleton;
}
/** Reset the singleton (test hook). */
export function __resetApiClientSingleton() {
    _singleton = undefined;
}
//# sourceMappingURL=client.js.map